import {
  AppShell,
  AuthLayout,
  Button,
  DropdownMenuItem,
  EmptyState,
  DropdownMenuSeparator,
  toast,
  useTheme,
  type CommandGroup,
  type NavGroup,
  type NavItem,
  type RenderLink,
} from '@bixo/ui'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import {
  Building2,
  CreditCard,
  KeyRound,
  LayoutDashboard,
  Lock,
  LogOut,
  Megaphone,
  Moon,
  Package,
  Plus,
  ScrollText,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide-react'
import { useMemo } from 'react'
import { useCan, type PlatformPermission } from '../lib/access'
import { endpoints, meQuery } from '../lib/api'
import { ChangePasswordForm } from './change-password'
import { platformBadges, platformBrand } from './auth-brand'

const PATHS = [
  '/',
  '/tenants',
  '/plans',
  '/billing',
  '/announcements',
  '/users',
  '/audit',
  '/settings',
  '/security',
] as const
type PlatformPath = (typeof PATHS)[number]

/** What a page needs; the API enforces the same permissions on every call. */
const PAGE_PERMISSION: Partial<Record<PlatformPath, PlatformPermission>> = {
  '/tenants': 'tenants.view',
  '/plans': 'plans.view',
  '/billing': 'billing.view',
  '/announcements': 'announcements.view',
  '/users': 'users.view',
  '/audit': 'audit.view',
  '/settings': 'settings.view',
}

const isPath = (path: string | undefined): path is PlatformPath =>
  (PATHS as readonly string[]).includes(path ?? '')

const renderLink: RenderLink = ({ item, className, children, onNavigate }) =>
  isPath(item.to) ? (
    <Link to={item.to} className={className} onClick={onNavigate}>
      {children}
    </Link>
  ) : null

export function PlatformLayout() {
  const { data: me } = useSuspenseQuery(meQuery)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toggle } = useTheme()
  const can = useCan()

  const signOut = async (): Promise<void> => {
    try {
      await endpoints.logout()
    } catch {
      /* already signed out */
    }
    queryClient.clear()
    toast.success('Signed out')
    await navigate({ to: '/login' })
  }

  const groups = useMemo<NavGroup[]>(() => {
    const live = (id: string, label: string, icon: NavItem['icon'], to: PlatformPath): NavItem[] => {
      const needs = PAGE_PERMISSION[to]
      if (needs && !can(needs)) return []
      return [{ id, label, icon, to, active: to === '/' ? pathname === '/' : pathname.startsWith(to) }]
    }
    return [
      {
        label: 'Platform',
        items: [
          ...live('overview', 'Overview', LayoutDashboard, '/'),
          ...live('tenants', 'Workspaces', Building2, '/tenants'),
        ],
      },
      {
        label: 'Commercial',
        items: [
          ...live('plans', 'Plans & features', Package, '/plans'),
          ...live('billing', 'Billing', CreditCard, '/billing'),
        ],
      },
      {
        label: 'Operations',
        items: [
          ...live('announcements', 'Announcements', Megaphone, '/announcements'),
          ...live('users', 'Users', Users, '/users'),
          ...live('audit', 'Platform audit', ScrollText, '/audit'),
          ...live('settings', 'Settings', Settings, '/settings'),
        ],
      },
    ].filter((group) => group.items.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, me?.permissions])

  const commands = useMemo<CommandGroup[]>(
    () => [
      {
        heading: 'Go to',
        items: [
          {
            id: 'overview',
            label: 'Overview',
            icon: LayoutDashboard,
            onSelect: () => void navigate({ to: '/' }),
          },
          {
            id: 'tenants',
            label: 'Workspaces',
            icon: Building2,
            onSelect: () => void navigate({ to: '/tenants' }),
          },
        ],
      },
      {
        heading: 'Actions',
        items: [
          ...(can('tenants.manage')
            ? [
                {
                  id: 'new-tenant',
                  label: 'Create workspace',
                  icon: Plus,
                  keywords: ['tenant', 'onboard', 'business'],
                  onSelect: () => void navigate({ to: '/tenants', search: { create: true } }),
                },
              ]
            : []),
          { id: 'theme', label: 'Toggle dark mode', icon: Moon, onSelect: toggle },
          { id: 'logout', label: 'Sign out', icon: LogOut, onSelect: () => void signOut() },
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, toggle, me?.permissions],
  )

  if (!me) return null

  // A temporary password (new account or admin reset) must be replaced first;
  // the API refuses everything else until then.
  if (me.must_change_password) {
    return (
      <AuthLayout
        brand={platformBrand}
        badges={platformBadges}
        title="Choose your own password"
        subtitle={`${me.email} is using a temporary password. Set a new one to continue.`}
        footer={
          <button
            type="button"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        }
        showcase={
          <>
            <KeyRound className="size-10 text-white/80" />
            <h2 className="mt-6 text-4xl leading-[1.1] font-semibold tracking-tight text-balance">
              Only you should know your platform password.
            </h2>
          </>
        }
      >
        <ChangePasswordForm currentLabel="Temporary password" />
      </AuthLayout>
    )
  }

  const page = PATHS.find((path) => path !== '/' && pathname.startsWith(path))
  const needed = page ? PAGE_PERMISSION[page] : undefined
  const allowed = !needed || can(needed)

  return (
    <AppShell
      brand={{ ...platformBrand, name: 'Bixo Platform', subtitle: 'Control centre' }}
      groups={groups}
      mobileTabs={[
        { id: 'm-overview', label: 'Overview', icon: LayoutDashboard, to: '/', active: pathname === '/' },
        {
          id: 'm-tenants',
          label: 'Workspaces',
          icon: Building2,
          to: '/tenants',
          active: pathname.startsWith('/tenants'),
        },
      ]}
      user={{ name: me.name, email: me.email }}
      renderLink={renderLink}
      commands={commands}
      userMenu={
        <>
          <DropdownMenuItem onSelect={() => void navigate({ to: '/security' })}>
            <ShieldCheck /> Account & security
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem destructive onSelect={() => void signOut()}>
            <LogOut /> Sign out
          </DropdownMenuItem>
        </>
      }
    >
      {allowed ? (
        <Outlet />
      ) : (
        <EmptyState
          icon={Lock}
          title="Your role does not include this page"
          description={`You are signed in as ${me.role_label}. Ask a platform Owner or Admin if you need access.`}
          action={
            <Button variant="secondary" onClick={() => void navigate({ to: '/' })}>
              Back to overview
            </Button>
          }
        />
      )}
    </AppShell>
  )
}
